import { useEffect, useState } from 'react'
import { onDemoSessionEnded } from '../demoSession.js'
import { fetchDemoWorkers, searchDemoWorkers, useDemoPeople, useMe, useOpenDemoSession } from '../index.js'
import type { SessionRole } from '../../core/index.js'
import { DemoPeopleModal, DemoRoleButton } from './DemoPeopleModal.js'
import { useDemoSessionLauncher } from './useDemoSessionLauncher.js'

/**
 * Inside a demo client's own shell, for a real sign-in: an operator may
 * open a tab as one of the client's facility administrators or workers, a
 * facility administrator as one of their workers. Downward only, and the
 * API decides - the list it gives is the reach. Hidden for a real client,
 * for a demo session, whose banner has its own switch, and unless a
 * SeeWhoCares admin has switched View as on for the client's staff.
 */
export function ViewAsMenu({ role }: { role: SessionRole }) {
  const meQuery = useMe()
  const [isOpen, setIsOpen] = useState(false)
  const isOffered = meQuery.data?.isDemo === true && meQuery.data.viewAs === true && !meQuery.data.demoSession && role !== 'worker'
  const peopleQuery = useDemoPeople(isOffered && isOpen)
  const open = useOpenDemoSession()
  const { launch, pending, error } = useDemoSessionLauncher((input) => open.mutateAsync(input))
  // The chooser has done its job once a session it opened has ended: close it, so coming back from the demo is not to a dialog still asking who to open.
  useEffect(() => (isOpen ? onDemoSessionEnded(false, () => setIsOpen(false)) : undefined), [isOpen])

  if (!isOffered) {
    return null
  }
  return (
    <>
      <DemoRoleButton label="View as…" tone="dark" showLabel onClick={() => setIsOpen(true)} />
      {isOpen && (
        <DemoPeopleModal
          title="View this demo as…"
          subtitle="Choosing someone opens a new tab signed in as them. You stay signed in here."
          people={peopleQuery.data}
          loadWorkers={fetchDemoWorkers}
          searchWorkers={searchDemoWorkers}
          workersKey="own-session"
          isLoading={peopleQuery.isPending}
          loadError={peopleQuery.isError ? 'Could not load the people.' : null}
          onChoose={(input) => void launch(input)}
          pending={pending}
          error={error}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
